import { generatePath } from 'react-router';
import get from 'lodash/get';
import pickBy from 'lodash/pickBy';
import AppStorage from '@Services/AppStorage';
import Register from '@Services/Register';
import RegisterData from '@Services/RegisterData';
import NavigationHistory from '@Services/NavigationHistory';
import loginConstants from '@Services/Login/LoginConstants';

import Catalyst from '@Services/Catalyst';
import { openConfirmationEmailSentDialog } from '@Pages/Login/RegisterConfirmation/RegisterConfirmationActions';
import Auth from '@Services/Auth';
import history from '@AppRoutes/history';
import { paths } from '@AppRoutes/constants';
import { closeDialog, updateDialog } from '@Containers/Dialogs/DialogsActions';
import { initialState } from './RegisterReducer';

import {
  registerSetIpAddressReducerType,
  registerSetIsLoadingReducerType,
  registerSetDataReducerType,
  registerSetRegisterFailReducerType,
  typeOfRegisterModal,
} from './RegisterConstants';

import { data as dataSelector, isShibboleth as isShibbolethSelector, registerFail as registerFailSelector } from './RegisterSelectors';
import { trackRegistrationErrors } from './analytics';

export function setIsLoading(isLoading) {
  return {
    type: registerSetIsLoadingReducerType,
    payload: { isLoading },
  };
}

export function setIpAddress(ipAddress) {
  return {
    type: registerSetIpAddressReducerType,
    payload: { ipAddress },
  };
}

export function setData(data) {
  return {
    type: registerSetDataReducerType,
    payload: data,
  };
}

export function setRegisterFail(registerFail) {
  return {
    type: registerSetRegisterFailReducerType,
    payload: registerFail,
  };
}

export const init = () => {
  return async (dispatch, getState) => {
    dispatch(setIsLoading(true));
    await Auth.getPreregisterInfo()
      .then((data) => {
        const existingData = dataSelector(getState());
        const firstName = get(data, 'firstName');
        const lastName = get(data, 'lastName');
        const email = get(data, 'email');
        const userName = get(data, 'username');
        const registrationType = get(data, 'registrationType');

        dispatch(
          setData(
            // Set only fields that are empty in current state
            pickBy(
              {
                firstName,
                lastName,
                email,
                userName,
                registrationType,
              },
              (value, key) => !existingData || !existingData[key]
            )
          )
        );
      })
      .finally(() => {
        dispatch(setIsLoading(false));
      })
      .catch(Logger.error);

    const data = Register.getData();
    dispatch(setData(data));

    await RegisterData.getIpAddress()
      .then((ip) => {
        dispatch(setIpAddress(ip));
      })
      .catch(Logger.error);
    Catalyst.trackRegistrationStarted();
  };
};

export const cancel = () => {
  return async () => {
    const stateWithParams = NavigationHistory.getLastVisitedState(...loginConstants.STATE_WHITELIST);
    if (stateWithParams) {
      history.push({
        pathname: stateWithParams.pathName,
        state: stateWithParams.params,
      });
    } else {
      Auth.goToLandingPage();
    }
  };
};

export const login = (loginData) => {
  return async () => {
    if (loginData) {
      // Case when email verification is disabled
      try {
        await Auth.login(loginData);
        Auth.goToLandingPage(true);
      } catch (e) {
        Logger.error(e);
      }
    } else {
      // Case when email verification is enabled
      history.push({
        pathname: generatePath(paths.LOGIN),
      });
    }
  };
};

export const submit = () => {
  return async (dispatch, getState) => {
    // const appState = store.getState();
    // const history = getRouteHistory(appState);
    const data = dataSelector(getState());
    const { rememberMe, userName, ...restData } = data;
    const requestData = restData;

    if (userName) {
      requestData.userName = userName;
    }

    if (data.marketingInfo) {
      Register.clear();
      Register.setData(requestData);
      if (history.location.pathname === paths.PRIVATE_REGISTER) {
        history.push({
          pathname: paths.PRIVATE_FINALIZE_REGISTER,
        });
      } else {
        history.push({
          pathname: paths.FINALIZE_REGISTER,
        });
      }
      return;
    }

    const isShibboleth = isShibbolethSelector(getState());
    const shibbolethInfo = AppStorage.instance.get('shibbolethInfo');

    if (isShibboleth) {
      requestData.shibbolethToken = shibbolethInfo.shibbolethToken;
    }

    // Reset error
    dispatch(setRegisterFail(initialState.get('registerFail')));
    dispatch(setIsLoading(true));

    try {
      const result = await RegisterData.register(requestData);

      Catalyst.trackRegistrationCompleted();

      if (result) {
        // Case when email verification is disabled
        let loginData;
        if (isShibboleth) {
          loginData = {
            providerId: shibbolethInfo.providerId,
            shibbolethToken: shibbolethInfo.shibbolethToken,
            grant_type: 'shibboleth',
          };
          AppStorage.instance.remove('shibbolethInfo');
        } else {
          loginData = {
            username: result.username,
            password: data.password,
          };
        }

        if (rememberMe) {
          loginData = _.extend(loginData, { remember_me: rememberMe });
        }

        if (Auth.getAccessToken()) {
          await Auth.logout();
        }
        await dispatch(login(loginData));
      } else {
        // Case when email verification is enabled
        dispatch(openConfirmationEmailSentDialog({ email: requestData.email }));
        await dispatch(login());
      }
    } catch (err) {
      Logger.error(err);
      trackRegistrationErrors([err.message], false);
      dispatch(setRegisterFail(err.message));
    }

    dispatch(setIsLoading(false));
  };
};

export const resetState = () => {
  return async (dispatch) => {
    dispatch(setData(initialState.get('data')));
    dispatch(setRegisterFail(initialState.get('registerFail')));
  };
};

export function openRegisterModal() {
  return (dispatch) => {
    dispatch(
      updateDialog({
        id: typeOfRegisterModal,
        dialogType: typeOfRegisterModal,
      })
    );
  };
}

export function closeRegisterModal() {
  return (dispatch) => {
    dispatch(
      closeDialog({
        id: typeOfRegisterModal,
      })
    );
  };
}

export const submitRegisterModal = () => async (dispatch, getState) => {
  await dispatch(submit());
  if (!registerFailSelector(getState())) {
    dispatch(closeRegisterModal());
  }
};
